import { ENABLE_ONDEMAND_ISR } from '@/constants';
import sleep from '@/utils/sleep';
import {
  errToString, Operation, RevalidateAPI, RevalidateResponses, SpaceAPI,
} from '@typedream/data';
import create, {
  SetState, GetState, StoreApi, Mutate,
} from 'zustand';
import { subscribeWithSelector, devtools } from 'zustand/middleware';

type IRevalidateStatus = {
  loading: boolean,
  endpoints: { [endpoint: string]: RevalidateResponses & { publishPercent: number } }
}
type IRevalidateStatusSlice = {
  status: IRevalidateStatus
  resetStatus: () => void
  checkStatus: (
    revalidateStatus: RevalidateResponses[]
  ) => Promise<IRevalidateStatus>
}

type StoreState = IRevalidateStatusSlice;

export type StoreSlice<T> = (
  set: SetState<StoreState>,
  get: GetState<StoreState>,
) => T;

const createRevalidateStatusStore: StoreSlice<IRevalidateStatusSlice> = (set) => {
  const setstatus = (status: IRevalidateStatus) => {
    set((state) => ({
      status: {
        ...state.status,
        ...status,
      },
    }));
  };

  const checkStatus = async (
    revalidateStatus: RevalidateResponses[],
  ): Promise<IRevalidateStatus> => {
    if (!ENABLE_ONDEMAND_ISR) {
      const status: IRevalidateStatus = {
        loading: false,
        endpoints: revalidateStatus?.reduce((e, v) => {
          e[v.endpoint] = {
            id: v.operation_id,
            created_at: 0,
            status: 'succeeded',
          };
          return e;
        }, {}),
      };

      setstatus(status);
      return status;
    }

    const status: IRevalidateStatus = {
      loading: true,
      endpoints: revalidateStatus?.reduce((e, v) => {
        e[v.endpoint] = v;
        return e;
      }, {}),
    };
    setstatus(status);

    const responses: {
      endpoint: string, op: Operation
    }[] = await Promise.all(revalidateStatus.map(async (revStatus) => {
      let finalResp: Operation = {
        id: revStatus.operation_id,
        created_at: 0,
        status: revStatus.status,
        reason_failed: revStatus.reason_failed,
      };

      while ((finalResp.status === 'running' || finalResp.status === 'not_started') && !!revStatus.operation_id) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const revResp = await RevalidateAPI.revalidateStatus(
            revStatus.endpoint,
            revStatus.operation_id,
          );
          status.endpoints[revStatus.endpoint].publishPercent = revResp.data.percent_complete;
          setstatus(status);
          finalResp = revResp.data;
        } catch (e) {
          finalResp = {
            ...finalResp,
            status: 'failed',
            reason_failed: errToString(e),
          };
        }

        // eslint-disable-next-line no-await-in-loop
        await sleep(1000);
      }
      return {
        endpoint: revStatus.endpoint,
        op: finalResp,
      };
    }));

    responses.forEach((r) => {
      status.endpoints[r.endpoint] = {
        endpoint: r.endpoint,
        operation_id: r.op.id,
        status: r.op.status,
        reason_failed: r.op.reason_failed,
        publishPercent: r.op.percent_complete,
      };
    });
    status.loading = false;
    setstatus(status);
    return status;
  };

  const resetStatus = () => {
    set(() => ({
      status: undefined,
    }));
  };

  const state: IRevalidateStatusSlice = {
    status: undefined,
    resetStatus,
    checkStatus,
  };
  return state;
};

export const useRevalidateStatusStore = create<
  StoreState,
  SetState<StoreState>,
  GetState<StoreState>,
  Mutate<StoreApi<StoreState>, [['zustand/subscribeWithSelector', never]]>
>(
  subscribeWithSelector(
    devtools(
      (set, get) => ({
        ...createRevalidateStatusStore(set, get),
      }),
    ),
  ),
);
